import clsx from 'clsx';
import React, { useState } from 'react';

import Button from '@components/Button';
import { PrimaryButton, SecondaryButton } from '@components/Button/variants';
import { TextButton } from '@components/Button/variants';
import IconArrowDown from '@components/Images/Rebranding/IconArrowDown';
import SvgIconFaceBook from '@components/Images/Rebranding/IconFacebook';
import SvgIconInstagram from '@components/Images/Rebranding/IconInstagram';
import SvgIconLinkedIn from '@components/Images/Rebranding/IconLinkedIn';
import SvgIconX from '@components/Images/Rebranding/IconX';
import { EmployeeProps } from '@utils/aboutInterfaces';

const Employee = ({ canSendMessage, member, onMessage }: EmployeeProps) => {
  const [isActive, setActive] = useState(false);
  const { name, title, bio, twitter, linkedinUrl, facebookUrl, instagram, recentArticle } = member;

  const buildSocialMediaLinks = () => {
    const socialMediaLinks = [];
    const iconClass =
      'h-24 w-24 items-center justify-center transition-colors hover:border-primary-magenta hover:text-primary-magenta focus:border-primary-magenta focus:text-primary-magenta';

    // Check if the social media profiles exist and push them into the array
    if (twitter)
      socialMediaLinks.push({
        link: `https://www.twitter.com/${twitter}`,
        icon: <SvgIconX className={iconClass} />,
      });
    if (linkedinUrl)
      socialMediaLinks.push({ link: linkedinUrl, icon: <SvgIconLinkedIn className={iconClass} /> });
    if (facebookUrl)
      socialMediaLinks.push({ link: facebookUrl, icon: <SvgIconFaceBook className={iconClass} /> });
    if (instagram)
      socialMediaLinks.push({
        link: `https://www.instagram.com/${instagram.replace('@', '')}`,
        icon: <SvgIconInstagram className={iconClass} />,
      });
    return socialMediaLinks;
  };

  return (
    <div className="mt-24 w-full">
      <div className="flex flex-col items-start gap-10 border-0 border-l border-r border-solid border-grayscale-charcoal px-28 py-8">
        <TextButton
          as="button"
          className="flex w-full !justify-start px-0 no-underline hover:!text-grayscale-charcoal focus:text-grayscale-charcoal"
          onClick={() => setActive(!isActive)}
          data-test="Employee"
        >
          <div className="flex w-full items-start justify-between text-left">
            <h4 className="my-0 font-suisse-works text-body-md font-normal no-underline">
              {name}
              <span className="block text-left font-suisse-intl text-icon-sm text-primary-magenta">
                {title}
              </span>
            </h4>
            <IconArrowDown
              className={clsx(
                'w-[17px] -rotate-90 transform fill-grayscale-charcoal transition-transform',
                {
                  'rotate-0': isActive,
                }
              )}
            />
          </div>
        </TextButton>
        {isActive && (
          <>
            <div className="mt-8 font-suisse-neue text-body-md">{bio}</div>
            {(twitter || linkedinUrl || facebookUrl || instagram) && (
              <div className="mt-32 flex gap-32">
                {buildSocialMediaLinks().map((link) => (
                  <Button
                    as="link"
                    href={link.link}
                    key={link.link}
                    className="flex items-center gap-8"
                    data-test="social-media-link"
                  >
                    {link.icon}
                  </Button>
                ))}
              </div>
            )}

            {(canSendMessage || recentArticle) && (
              <div className="mt-32 flex gap-8">
                {canSendMessage && (
                  <PrimaryButton
                    as="button"
                    className="px-24 py-8 font-suisse-intl text-button-sm focus-visible:ring-0 max-sm:hover:!bg-transparent"
                    onClick={onMessage}
                    data-test="message-button"
                  >
                    Message
                  </PrimaryButton>
                )}
                {recentArticle && (
                  <SecondaryButton
                    as="link"
                    href={member.recentArticle}
                    data-test="recent-article-link"
                    size="small"
                    className="!px-12 py-8 font-suisse-intl text-button-sm focus:bg-transparent focus-visible:ring-0 md:px-24"
                  >
                    View recent articles
                  </SecondaryButton>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Employee;
